/**
 * i18n for config form field labels (aligned with src/config/schema.ts FIELD_LABELS).
 * Backend sends uiHints with path -> English label; we resolve the display label
 * by path so the UI can show the current locale.
 */

import type { Locale } from "./strings.js";
import { getLocale } from "./strings.js";
import { pathKey } from "./views/config-form.shared.js";

function lookupKeys(path: Array<string | number>): string[] {
  const keyExact = pathKey(path);
  const keyWild = path.map((s) => (typeof s === "number" ? "*" : s)).join(".");
  const keyBracket = keyWild.replace(/\.\*/g, "[]");
  return [keyExact, keyWild, keyBracket];
}

const CONFIG_FIELD_LABELS: Record<Locale, Record<string, string>> = {
  en: {
    "meta.lastTouchedVersion": "Config Last Touched Version",
    "meta.lastTouchedAt": "Config Last Touched At",
    "update.channel": "Update Channel",
    "update.checkOnStart": "Update Check on Start",
    "diagnostics.enabled": "Diagnostics Enabled",
    "diagnostics.flags": "Diagnostics Flags",
    "diagnostics.otel.enabled": "OpenTelemetry Enabled",
    "diagnostics.otel.endpoint": "OpenTelemetry Endpoint",
    "diagnostics.otel.protocol": "OpenTelemetry Protocol",
    "diagnostics.otel.headers": "OpenTelemetry Headers",
    "diagnostics.otel.serviceName": "OpenTelemetry Service Name",
    "diagnostics.otel.traces": "OpenTelemetry Traces Enabled",
    "diagnostics.otel.metrics": "OpenTelemetry Metrics Enabled",
    "diagnostics.otel.logs": "OpenTelemetry Logs Enabled",
    "diagnostics.otel.sampleRate": "OpenTelemetry Trace Sample Rate",
    "diagnostics.otel.flushIntervalMs": "OpenTelemetry Flush Interval (ms)",
    "diagnostics.cacheTrace.enabled": "Cache Trace Enabled",
    "diagnostics.cacheTrace.filePath": "Cache Trace File Path",
    "diagnostics.cacheTrace.includeMessages": "Cache Trace Include Messages",
    "diagnostics.cacheTrace.includePrompt": "Cache Trace Include Prompt",
    "diagnostics.cacheTrace.includeSystem": "Cache Trace Include System",
    "agents.list.*.identity.avatar": "Identity Avatar",
    "agents.list.*.skills": "Agent Skill Filter",
    "gateway.remote.url": "Remote Gateway URL",
    "gateway.remote.sshTarget": "Remote Gateway SSH Target",
    "gateway.remote.sshIdentity": "Remote Gateway SSH Identity",
    "gateway.remote.token": "Remote Gateway Token",
    "gateway.remote.password": "Remote Gateway Password",
    "gateway.remote.tlsFingerprint": "Remote Gateway TLS Fingerprint",
    "gateway.auth.token": "Gateway Token",
    "gateway.auth.password": "Gateway Password",
    "tools.media.image.enabled": "Enable Image Understanding",
    "tools.media.image.maxBytes": "Image Understanding Max Bytes",
    "tools.media.image.maxChars": "Image Understanding Max Chars",
    "tools.media.image.prompt": "Image Understanding Prompt",
    "tools.media.image.timeoutSeconds": "Image Understanding Timeout (sec)",
    "tools.media.image.attachments": "Image Understanding Attachment Policy",
    "tools.media.image.models": "Image Understanding Models",
    "tools.media.image.scope": "Image Understanding Scope",
    "tools.media.models": "Media Understanding Shared Models",
    "tools.media.concurrency": "Media Understanding Concurrency",
    "tools.media.audio.enabled": "Enable Audio Understanding",
    "tools.media.audio.maxBytes": "Audio Understanding Max Bytes",
    "tools.media.audio.maxChars": "Audio Understanding Max Chars",
    "tools.media.audio.prompt": "Audio Understanding Prompt",
    "tools.media.audio.timeoutSeconds": "Audio Understanding Timeout (sec)",
    "tools.media.audio.language": "Audio Understanding Language",
    "tools.media.audio.attachments": "Audio Understanding Attachment Policy",
    "tools.media.audio.models": "Audio Understanding Models",
    "tools.media.audio.scope": "Audio Understanding Scope",
    "tools.media.video.enabled": "Enable Video Understanding",
    "tools.media.video.maxBytes": "Video Understanding Max Bytes",
    "tools.media.video.maxChars": "Video Understanding Max Chars",
    "tools.media.video.prompt": "Video Understanding Prompt",
    "tools.media.video.timeoutSeconds": "Video Understanding Timeout (sec)",
    "tools.media.video.attachments": "Video Understanding Attachment Policy",
    "tools.media.video.models": "Video Understanding Models",
    "tools.media.video.scope": "Video Understanding Scope",
    "tools.links.enabled": "Enable Link Understanding",
    "tools.links.maxLinks": "Link Understanding Max Links",
    "tools.links.timeoutSeconds": "Link Understanding Timeout (sec)",
    "tools.links.models": "Link Understanding Models",
    "tools.links.scope": "Link Understanding Scope",
    "tools.profile": "Tool Profile",
    "tools.alsoAllow": "Tool Allowlist Additions",
    "agents.list[].tools.profile": "Agent Tool Profile",
    "agents.list[].tools.alsoAllow": "Agent Tool Allowlist Additions",
    "tools.byProvider": "Tool Policy by Provider",
    "agents.list[].tools.byProvider": "Agent Tool Policy by Provider",
    "tools.exec.applyPatch.enabled": "Enable apply_patch",
    "tools.exec.applyPatch.allowModels": "apply_patch Model Allowlist",
    "tools.exec.notifyOnExit": "Exec Notify On Exit",
    "tools.exec.approvalRunningNoticeMs": "Exec Approval Running Notice (ms)",
    "tools.exec.host": "Exec Host",
    "tools.exec.security": "Exec Security",
    "tools.exec.ask": "Exec Ask",
    "tools.exec.node": "Exec Node Binding",
    "tools.exec.pathPrepend": "Exec PATH Prepend",
    "tools.exec.safeBins": "Exec Safe Bins",
    "tools.message.allowCrossContextSend": "Allow Cross-Context Messaging",
    "tools.message.crossContext.allowWithinProvider": "Allow Cross-Context (Same Provider)",
    "tools.message.crossContext.allowAcrossProviders": "Allow Cross-Context (Across Providers)",
    "tools.message.crossContext.marker.enabled": "Cross-Context Marker",
    "tools.message.crossContext.marker.prefix": "Cross-Context Marker Prefix",
    "tools.message.crossContext.marker.suffix": "Cross-Context Marker Suffix",
    "tools.message.broadcast.enabled": "Enable Message Broadcast",
    "tools.web.search.enabled": "Enable Web Search Tool",
    "tools.web.search.provider": "Web Search Provider",
    "tools.web.search.apiKey": "Brave Search API Key",
    "tools.web.search.maxResults": "Web Search Max Results",
    "tools.web.search.timeoutSeconds": "Web Search Timeout (sec)",
    "tools.web.search.cacheTtlMinutes": "Web Search Cache TTL (min)",
    "tools.web.fetch.enabled": "Enable Web Fetch Tool",
    "tools.web.fetch.maxChars": "Web Fetch Max Chars",
    "tools.web.fetch.timeoutSeconds": "Web Fetch Timeout (sec)",
    "tools.web.fetch.cacheTtlMinutes": "Web Fetch Cache TTL (min)",
    "tools.web.fetch.maxRedirects": "Web Fetch Max Redirects",
    "tools.web.fetch.userAgent": "Web Fetch User-Agent",
    "gateway.controlUi.basePath": "Control UI Base Path",
    "gateway.controlUi.root": "Control UI Assets Root",
    "gateway.controlUi.allowedOrigins": "Control UI Allowed Origins",
    "gateway.controlUi.allowInsecureAuth": "Allow Insecure Control UI Auth",
    "gateway.controlUi.dangerouslyDisableDeviceAuth": "Dangerously Disable Control UI Device Auth",
    "gateway.http.endpoints.chatCompletions.enabled": "OpenAI Chat Completions Endpoint",
    "gateway.reload.mode": "Config Reload Mode",
    "gateway.reload.debounceMs": "Config Reload Debounce (ms)",
    "gateway.nodes.browser.mode": "Gateway Node Browser Mode",
    "gateway.nodes.browser.node": "Gateway Node Browser Pin",
    "gateway.nodes.allowCommands": "Gateway Node Allowlist (Extra Commands)",
    "gateway.nodes.denyCommands": "Gateway Node Denylist",
    "nodeHost.browserProxy.enabled": "Node Browser Proxy Enabled",
    "nodeHost.browserProxy.allowProfiles": "Node Browser Proxy Allowed Profiles",
    "skills.load.watch": "Watch Skills",
    "skills.load.watchDebounceMs": "Skills Watch Debounce (ms)",
    "agents.defaults.workspace": "Workspace",
    "agents.defaults.repoRoot": "Repo Root",
    "agents.defaults.bootstrapMaxChars": "Bootstrap Max Chars",
    "agents.defaults.envelopeTimezone": "Envelope Timezone",
    "agents.defaults.envelopeTimestamp": "Envelope Timestamp",
    "agents.defaults.envelopeElapsed": "Envelope Elapsed",
    "agents.defaults.knowledge": "Knowledge Vault",
    "agents.defaults.knowledge.vaultDir": "Vault Directory Path",
    memory: "Memory",
    "memory.backend": "Memory Backend",
    "memory.citations": "Memory Citations Mode",
    "memory.qmd.command": "QMD Binary",
    "memory.qmd.includeDefaultMemory": "QMD Include Default Memory",
    "memory.qmd.paths": "QMD Extra Paths",
    "memory.qmd.paths.path": "QMD Path",
    "memory.qmd.paths.pattern": "QMD Path Pattern",
    "memory.qmd.paths.name": "QMD Path Name",
    "memory.qmd.sessions.enabled": "QMD Session Indexing",
    "memory.qmd.sessions.exportDir": "QMD Session Export Directory",
    "memory.qmd.sessions.retentionDays": "QMD Session Retention (days)",
    "memory.qmd.update.interval": "QMD Update Interval",
    "memory.qmd.update.debounceMs": "QMD Update Debounce (ms)",
    "memory.qmd.update.onBoot": "QMD Update on Startup",
    "memory.qmd.update.embedInterval": "QMD Embed Interval",
    "memory.qmd.limits.maxResults": "QMD Max Results",
    "memory.qmd.limits.maxSnippetChars": "QMD Max Snippet Chars",
    "memory.qmd.limits.maxInjectedChars": "QMD Max Injected Chars",
    "memory.qmd.limits.timeoutMs": "QMD Search Timeout (ms)",
    "memory.qmd.scope": "QMD Surface Scope",
    "auth.profiles": "Auth Profiles",
    "auth.order": "Auth Profile Order",
    "auth.cooldowns.billingBackoffHours": "Billing Backoff (hours)",
    "auth.cooldowns.billingBackoffHoursByProvider": "Billing Backoff Overrides",
    "auth.cooldowns.billingMaxHours": "Billing Backoff Cap (hours)",
    "auth.cooldowns.failureWindowHours": "Failover Window (hours)",
    "agents.defaults.models": "Models",
    "agents.defaults.model.primary": "Primary Model",
    "agents.defaults.model.fallbacks": "Model Fallbacks",
    "agents.defaults.imageModel.primary": "Image Model",
    "agents.defaults.imageModel.fallbacks": "Image Model Fallbacks",
    "agents.defaults.humanDelay.mode": "Human Delay Mode",
    "agents.defaults.humanDelay.minMs": "Human Delay Min (ms)",
    "agents.defaults.humanDelay.maxMs": "Human Delay Max (ms)",
    "agents.defaults.cliBackends": "CLI Backends",
    "commands.native": "Native Commands",
    "commands.nativeSkills": "Native Skill Commands",
    "commands.text": "Text Commands",
    "commands.bash": "Allow Bash Chat Command",
    "commands.bashForegroundMs": "Bash Foreground Window (ms)",
    "commands.config": "Allow /config",
    "commands.debug": "Allow /debug",
    "commands.restart": "Allow Restart",
    "commands.useAccessGroups": "Use Access Groups",
    "commands.ownerAllowFrom": "Command Owners",
    "ui.seamColor": "Accent Color",
    "ui.assistant.name": "Assistant Name",
    "ui.assistant.avatar": "Assistant Avatar",
    "browser.evaluateEnabled": "Browser Evaluate Enabled",
    "browser.snapshotDefaults": "Browser Snapshot Defaults",
    "browser.snapshotDefaults.mode": "Browser Snapshot Mode",
    "browser.remoteCdpTimeoutMs": "Remote CDP Timeout (ms)",
    "browser.remoteCdpHandshakeTimeoutMs": "Remote CDP Handshake Timeout (ms)",
    "session.dmScope": "DM Session Scope",
    "session.agentToAgent.maxPingPongTurns": "Agent-to-Agent Ping-Pong Turns",
    "messages.ackReaction": "Ack Reaction Emoji",
    "messages.ackReactionScope": "Ack Reaction Scope",
    "messages.inbound.debounceMs": "Inbound Message Debounce (ms)",
    "talk.apiKey": "Talk API Key",
    "channels.whatsapp": "WhatsApp",
    "channels.telegram": "Telegram",
    "channels.telegram.customCommands": "Telegram Custom Commands",
    "channels.discord": "Discord",
    "channels.slack": "Slack",
    "channels.mattermost": "Mattermost",
    "channels.signal": "Signal",
    "channels.imessage": "iMessage",
    "channels.bluebubbles": "BlueBubbles",
    "channels.msteams": "MS Teams",
    "channels.telegram.botToken": "Telegram Bot Token",
    "channels.telegram.dmPolicy": "Telegram DM Policy",
    "channels.telegram.streamMode": "Telegram Draft Stream Mode",
    "channels.telegram.draftChunk.minChars": "Telegram Draft Chunk Min Chars",
    "channels.telegram.draftChunk.maxChars": "Telegram Draft Chunk Max Chars",
    "channels.telegram.draftChunk.breakPreference": "Telegram Draft Chunk Break Preference",
    "channels.telegram.retry.attempts": "Telegram Retry Attempts",
    "channels.telegram.retry.minDelayMs": "Telegram Retry Min Delay (ms)",
    "channels.telegram.retry.maxDelayMs": "Telegram Retry Max Delay (ms)",
    "channels.telegram.retry.jitter": "Telegram Retry Jitter",
    "channels.telegram.network.autoSelectFamily": "Telegram autoSelectFamily",
    "channels.telegram.timeoutSeconds": "Telegram API Timeout (seconds)",
    "channels.telegram.capabilities.inlineButtons": "Telegram Inline Buttons",
    "channels.whatsapp.dmPolicy": "WhatsApp DM Policy",
    "channels.whatsapp.selfChatMode": "WhatsApp Self-Phone Mode",
    "channels.whatsapp.debounceMs": "WhatsApp Message Debounce (ms)",
    "channels.signal.dmPolicy": "Signal DM Policy",
    "channels.imessage.dmPolicy": "iMessage DM Policy",
    "channels.bluebubbles.dmPolicy": "BlueBubbles DM Policy",
    "channels.discord.dm.policy": "Discord DM Policy",
    "channels.discord.retry.attempts": "Discord Retry Attempts",
    "channels.discord.retry.minDelayMs": "Discord Retry Min Delay (ms)",
    "channels.discord.retry.maxDelayMs": "Discord Retry Max Delay (ms)",
    "channels.discord.retry.jitter": "Discord Retry Jitter",
    "channels.discord.maxLinesPerMessage": "Discord Max Lines Per Message",
    "channels.discord.intents.presence": "Discord Presence Intent",
    "channels.discord.intents.guildMembers": "Discord Guild Members Intent",
    "channels.discord.pluralkit.enabled": "Discord PluralKit Enabled",
    "channels.discord.pluralkit.token": "Discord PluralKit Token",
    "channels.slack.dm.policy": "Slack DM Policy",
    "channels.slack.allowBots": "Slack Allow Bot Messages",
    "channels.discord.token": "Discord Bot Token",
    "channels.slack.botToken": "Slack Bot Token",
    "channels.slack.appToken": "Slack App Token",
    "channels.slack.userToken": "Slack User Token",
    "channels.slack.userTokenReadOnly": "Slack User Token Read Only",
    "channels.slack.thread.historyScope": "Slack Thread History Scope",
    "channels.slack.thread.inheritParent": "Slack Thread Parent Inheritance",
    "channels.mattermost.botToken": "Mattermost Bot Token",
    "channels.mattermost.baseUrl": "Mattermost Base URL",
    "channels.mattermost.chatmode": "Mattermost Chat Mode",
    "channels.mattermost.oncharPrefixes": "Mattermost Onchar Prefixes",
    "channels.mattermost.requireMention": "Mattermost Require Mention",
    "channels.signal.account": "Signal Account",
    "channels.imessage.cliPath": "iMessage CLI Path",
    "agents.list[].skills": "Agent Skill Filter",
    "agents.list[].identity.avatar": "Agent Avatar",
    "discovery.mdns.mode": "mDNS Discovery Mode",
    "plugins.enabled": "Enable Plugins",
    "plugins.allow": "Plugin Allowlist",
    "plugins.deny": "Plugin Denylist",
    "plugins.load.paths": "Plugin Load Paths",
    "plugins.slots": "Plugin Slots",
    "plugins.slots.memory": "Memory Plugin",
    "plugins.entries": "Plugin Entries",
    "plugins.entries.*.enabled": "Plugin Enabled",
    "plugins.entries.*.config": "Plugin Config",
    "plugins.installs": "Plugin Install Records",
    "plugins.installs.*.source": "Plugin Install Source",
    "plugins.installs.*.spec": "Plugin Install Spec",
    "plugins.installs.*.sourcePath": "Plugin Install Source Path",
    "plugins.installs.*.installPath": "Plugin Install Path",
    "plugins.installs.*.version": "Plugin Install Version",
    "plugins.installs.*.installedAt": "Plugin Install Time",
  },
  zh: {
    "meta.lastTouchedVersion": "配置最后触及版本",
    "meta.lastTouchedAt": "配置最后触及时间",
    "update.channel": "更新渠道",
    "update.checkOnStart": "启动时检查更新",
    "diagnostics.enabled": "诊断已启用",
    "diagnostics.flags": "诊断标志",
    "diagnostics.otel.enabled": "OpenTelemetry 已启用",
    "diagnostics.otel.endpoint": "OpenTelemetry 端点",
    "diagnostics.otel.protocol": "OpenTelemetry 协议",
    "diagnostics.otel.headers": "OpenTelemetry 请求头",
    "diagnostics.otel.serviceName": "OpenTelemetry 服务名",
    "diagnostics.otel.traces": "OpenTelemetry 链路已启用",
    "diagnostics.otel.metrics": "OpenTelemetry 指标已启用",
    "diagnostics.otel.logs": "OpenTelemetry 日志已启用",
    "diagnostics.otel.sampleRate": "OpenTelemetry 采样率",
    "diagnostics.otel.flushIntervalMs": "OpenTelemetry 刷新间隔（毫秒）",
    "diagnostics.cacheTrace.enabled": "缓存追踪已启用",
    "diagnostics.cacheTrace.filePath": "缓存追踪文件路径",
    "diagnostics.cacheTrace.includeMessages": "缓存追踪包含消息",
    "diagnostics.cacheTrace.includePrompt": "缓存追踪包含提示",
    "diagnostics.cacheTrace.includeSystem": "缓存追踪包含系统",
    "agents.list.*.identity.avatar": "身份头像",
    "agents.list.*.skills": "代理技能筛选",
    "gateway.remote.url": "远程网关 URL",
    "gateway.remote.sshTarget": "远程网关 SSH 目标",
    "gateway.remote.sshIdentity": "远程网关 SSH 身份",
    "gateway.remote.token": "远程网关令牌",
    "gateway.remote.password": "远程网关密码",
    "gateway.remote.tlsFingerprint": "远程网关 TLS 指纹",
    "gateway.auth.token": "网关令牌(访问远程服务需要从~/.openocta/openocta.json中获取)",
    "gateway.auth.password": "网关密码",
    "tools.media.image.enabled": "启用图像理解",
    "tools.media.image.maxBytes": "图像理解最大字节",
    "tools.media.image.maxChars": "图像理解最大字符",
    "tools.media.image.prompt": "图像理解提示",
    "tools.media.image.timeoutSeconds": "图像理解超时（秒）",
    "tools.media.image.attachments": "图像理解附件策略",
    "tools.media.image.models": "图像理解模型",
    "tools.media.image.scope": "图像理解范围",
    "tools.media.models": "媒体理解共享模型",
    "tools.media.concurrency": "媒体理解并发数",
    "tools.media.audio.enabled": "启用音频理解",
    "tools.media.audio.maxBytes": "音频理解最大字节",
    "tools.media.audio.maxChars": "音频理解最大字符",
    "tools.media.audio.prompt": "音频理解提示",
    "tools.media.audio.timeoutSeconds": "音频理解超时（秒）",
    "tools.media.audio.language": "音频理解语言",
    "tools.media.audio.attachments": "音频理解附件策略",
    "tools.media.audio.models": "音频理解模型",
    "tools.media.audio.scope": "音频理解范围",
    "tools.media.video.enabled": "启用视频理解",
    "tools.media.video.maxBytes": "视频理解最大字节",
    "tools.media.video.maxChars": "视频理解最大字符",
    "tools.media.video.prompt": "视频理解提示",
    "tools.media.video.timeoutSeconds": "视频理解超时（秒）",
    "tools.media.video.attachments": "视频理解附件策略",
    "tools.media.video.models": "视频理解模型",
    "tools.media.video.scope": "视频理解范围",
    "tools.links.enabled": "启用链接理解",
    "tools.links.maxLinks": "链接理解最大数量",
    "tools.links.timeoutSeconds": "链接理解超时（秒）",
    "tools.links.models": "链接理解模型",
    "tools.links.scope": "链接理解范围",
    "tools.profile": "工具配置集",
    "tools.alsoAllow": "工具允许列表附加",
    "agents.list[].tools.profile": "代理工具配置集",
    "agents.list[].tools.alsoAllow": "代理工具允许列表附加",
    "tools.byProvider": "按提供方的工具策略",
    "agents.list[].tools.byProvider": "代理按提供方的工具策略",
    "tools.exec.applyPatch.enabled": "启用 apply_patch",
    "tools.exec.applyPatch.allowModels": "apply_patch 模型允许列表",
    "tools.exec.notifyOnExit": "Exec 退出时通知",
    "tools.exec.approvalRunningNoticeMs": "Exec 审批运行提示（毫秒）",
    "tools.exec.host": "Exec 主机",
    "tools.exec.security": "Exec 安全",
    "tools.exec.ask": "Exec 询问",
    "tools.exec.node": "Exec 节点绑定",
    "tools.exec.pathPrepend": "Exec PATH 前置",
    "tools.exec.safeBins": "Exec 安全二进制",
    "tools.message.allowCrossContextSend": "允许跨上下文发送",
    "tools.message.crossContext.allowWithinProvider": "允许跨上下文（同提供方）",
    "tools.message.crossContext.allowAcrossProviders": "允许跨上下文（跨提供方）",
    "tools.message.crossContext.marker.enabled": "跨上下文标记",
    "tools.message.crossContext.marker.prefix": "跨上下文标记前缀",
    "tools.message.crossContext.marker.suffix": "跨上下文标记后缀",
    "tools.message.broadcast.enabled": "启用消息广播",
    "tools.web.search.enabled": "启用网页搜索工具",
    "tools.web.search.provider": "网页搜索提供方",
    "tools.web.search.apiKey": "Brave 搜索 API 密钥",
    "tools.web.search.maxResults": "网页搜索最大结果数",
    "tools.web.search.timeoutSeconds": "网页搜索超时（秒）",
    "tools.web.search.cacheTtlMinutes": "网页搜索缓存 TTL（分钟）",
    "tools.web.fetch.enabled": "启用网页抓取工具",
    "tools.web.fetch.maxChars": "网页抓取最大字符",
    "tools.web.fetch.timeoutSeconds": "网页抓取超时（秒）",
    "tools.web.fetch.cacheTtlMinutes": "网页抓取缓存 TTL（分钟）",
    "tools.web.fetch.maxRedirects": "网页抓取最大重定向",
    "tools.web.fetch.userAgent": "网页抓取 User-Agent",
    "gateway.controlUi.basePath": "控制台 UI 基础路径",
    "gateway.controlUi.root": "控制台 UI 资源根目录",
    "gateway.controlUi.allowedOrigins": "控制台 UI 允许来源",
    "gateway.controlUi.allowInsecureAuth": "允许控制台 UI 非安全认证",
    "gateway.controlUi.dangerouslyDisableDeviceAuth": "危险：禁用控制台 UI 设备认证",
    "gateway.http.endpoints.chatCompletions.enabled": "OpenAI 对话补全端点",
    "gateway.reload.mode": "配置重载模式",
    "gateway.reload.debounceMs": "配置重载防抖（毫秒）",
    "gateway.nodes.browser.mode": "网关节点浏览器模式",
    "gateway.nodes.browser.node": "网关节点浏览器固定",
    "gateway.nodes.allowCommands": "网关节点允许列表（额外命令）",
    "gateway.nodes.denyCommands": "网关节点拒绝列表",
    "nodeHost.browserProxy.enabled": "节点浏览器代理已启用",
    "nodeHost.browserProxy.allowProfiles": "节点浏览器代理允许配置集",
    "skills.load.watch": "监听技能",
    "skills.load.watchDebounceMs": "技能监听防抖（毫秒）",
    "agents.defaults.workspace": "工作区",
    "agents.defaults.repoRoot": "仓库根目录",
    "agents.defaults.bootstrapMaxChars": "引导最大字符",
    "agents.defaults.envelopeTimezone": "信封时区",
    "agents.defaults.envelopeTimestamp": "信封时间戳",
    "agents.defaults.envelopeElapsed": "信封耗时",
    "agents.defaults.knowledge": "知识库 Vault",
    "agents.defaults.knowledge.vaultDir": "Vault 目录路径",
    memory: "记忆",
    "memory.backend": "记忆后端",
    "memory.citations": "记忆引用模式",
    "memory.qmd.command": "QMD 可执行文件",
    "memory.qmd.includeDefaultMemory": "QMD 包含默认记忆",
    "memory.qmd.paths": "QMD 额外路径",
    "memory.qmd.paths.path": "QMD 路径",
    "memory.qmd.paths.pattern": "QMD 路径模式",
    "memory.qmd.paths.name": "QMD 路径名称",
    "memory.qmd.sessions.enabled": "QMD 会话索引",
    "memory.qmd.sessions.exportDir": "QMD 会话导出目录",
    "memory.qmd.sessions.retentionDays": "QMD 会话保留（天）",
    "memory.qmd.update.interval": "QMD 更新间隔",
    "memory.qmd.update.debounceMs": "QMD 更新防抖（毫秒）",
    "memory.qmd.update.onBoot": "QMD 启动时更新",
    "memory.qmd.update.embedInterval": "QMD 嵌入间隔",
    "memory.qmd.limits.maxResults": "QMD 最大结果数",
    "memory.qmd.limits.maxSnippetChars": "QMD 最大片段字符",
    "memory.qmd.limits.maxInjectedChars": "QMD 最大注入字符",
    "memory.qmd.limits.timeoutMs": "QMD 搜索超时（毫秒）",
    "memory.qmd.scope": "QMD 作用范围",
    "auth.profiles": "认证配置集",
    "auth.order": "认证配置顺序",
    "auth.cooldowns.billingBackoffHours": "计费退避（小时）",
    "auth.cooldowns.billingBackoffHoursByProvider": "计费退避覆盖",
    "auth.cooldowns.billingMaxHours": "计费退避上限（小时）",
    "auth.cooldowns.failureWindowHours": "故障窗口（小时）",
    "agents.defaults.models": "模型",
    "agents.defaults.model.primary": "主模型",
    "agents.defaults.model.fallbacks": "模型回退",
    "agents.defaults.imageModel.primary": "图像模型",
    "agents.defaults.imageModel.fallbacks": "图像模型回退",
    "agents.defaults.humanDelay.mode": "人工延迟模式",
    "agents.defaults.humanDelay.minMs": "人工延迟最小（毫秒）",
    "agents.defaults.humanDelay.maxMs": "人工延迟最大（毫秒）",
    "agents.defaults.cliBackends": "CLI 后端",
    "commands.native": "原生命令",
    "commands.nativeSkills": "原生技能命令",
    "commands.text": "文本命令",
    "commands.bash": "允许 Bash 聊天命令",
    "commands.bashForegroundMs": "Bash 前台窗口（毫秒）",
    "commands.config": "允许 /config",
    "commands.debug": "允许 /debug",
    "commands.restart": "允许重启",
    "commands.useAccessGroups": "使用访问组",
    "commands.ownerAllowFrom": "命令所有者",
    "ui.seamColor": "强调色",
    "ui.assistant.name": "助手名称",
    "ui.assistant.avatar": "助手头像",
    "browser.evaluateEnabled": "浏览器执行已启用",
    "browser.snapshotDefaults": "浏览器快照默认",
    "browser.snapshotDefaults.mode": "浏览器快照模式",
    "browser.remoteCdpTimeoutMs": "远程 CDP 超时（毫秒）",
    "browser.remoteCdpHandshakeTimeoutMs": "远程 CDP 握手超时（毫秒）",
    "session.dmScope": "私信会话范围",
    "session.agentToAgent.maxPingPongTurns": "代理间乒乓轮数",
    "messages.ackReaction": "确认反应表情",
    "messages.ackReactionScope": "确认反应范围",
    "messages.inbound.debounceMs": "入站消息防抖（毫秒）",
    "talk.apiKey": "语音 API 密钥",
    "channels.whatsapp": "WhatsApp",
    "channels.telegram": "Telegram",
    "channels.telegram.customCommands": "Telegram 自定义命令",
    "channels.discord": "Discord",
    "channels.slack": "Slack",
    "channels.mattermost": "Mattermost",
    "channels.signal": "Signal",
    "channels.imessage": "iMessage",
    "channels.bluebubbles": "BlueBubbles",
    "channels.msteams": "MS Teams",
    "channels.telegram.botToken": "Telegram 机器人令牌",
    "channels.telegram.dmPolicy": "Telegram 私信策略",
    "channels.telegram.streamMode": "Telegram 草稿流模式",
    "channels.telegram.draftChunk.minChars": "Telegram 草稿块最小字符",
    "channels.telegram.draftChunk.maxChars": "Telegram 草稿块最大字符",
    "channels.telegram.draftChunk.breakPreference": "Telegram 草稿块断行偏好",
    "channels.telegram.retry.attempts": "Telegram 重试次数",
    "channels.telegram.retry.minDelayMs": "Telegram 重试最小延迟（毫秒）",
    "channels.telegram.retry.maxDelayMs": "Telegram 重试最大延迟（毫秒）",
    "channels.telegram.retry.jitter": "Telegram 重试抖动",
    "channels.telegram.network.autoSelectFamily": "Telegram autoSelectFamily",
    "channels.telegram.timeoutSeconds": "Telegram API 超时（秒）",
    "channels.telegram.capabilities.inlineButtons": "Telegram 内联按钮",
    "channels.whatsapp.dmPolicy": "WhatsApp 私信策略",
    "channels.whatsapp.selfChatMode": "WhatsApp 自聊模式",
    "channels.whatsapp.debounceMs": "WhatsApp 消息防抖（毫秒）",
    "channels.signal.dmPolicy": "Signal 私信策略",
    "channels.imessage.dmPolicy": "iMessage 私信策略",
    "channels.bluebubbles.dmPolicy": "BlueBubbles 私信策略",
    "channels.discord.dm.policy": "Discord 私信策略",
    "channels.discord.retry.attempts": "Discord 重试次数",
    "channels.discord.retry.minDelayMs": "Discord 重试最小延迟（毫秒）",
    "channels.discord.retry.maxDelayMs": "Discord 重试最大延迟（毫秒）",
    "channels.discord.retry.jitter": "Discord 重试抖动",
    "channels.discord.maxLinesPerMessage": "Discord 每消息最大行数",
    "channels.discord.intents.presence": "Discord 在线状态意图",
    "channels.discord.intents.guildMembers": "Discord 频道成员意图",
    "channels.discord.pluralkit.enabled": "Discord PluralKit 已启用",
    "channels.discord.pluralkit.token": "Discord PluralKit 令牌",
    "channels.slack.dm.policy": "Slack 私信策略",
    "channels.slack.allowBots": "Slack 允许机器人消息",
    "channels.discord.token": "Discord 机器人令牌",
    "channels.slack.botToken": "Slack 机器人令牌",
    "channels.slack.appToken": "Slack 应用令牌",
    "channels.slack.userToken": "Slack 用户令牌",
    "channels.slack.userTokenReadOnly": "Slack 用户令牌只读",
    "channels.slack.thread.historyScope": "Slack 线程历史范围",
    "channels.slack.thread.inheritParent": "Slack 线程继承父级",
    "channels.mattermost.botToken": "Mattermost 机器人令牌",
    "channels.mattermost.baseUrl": "Mattermost Base URL",
    "channels.mattermost.chatmode": "Mattermost 聊天模式",
    "channels.mattermost.oncharPrefixes": "Mattermost 触发前缀",
    "channels.mattermost.requireMention": "Mattermost 需要 @ 提及",
    "channels.signal.account": "Signal 账号",
    "channels.imessage.cliPath": "iMessage CLI 路径",
    "agents.list[].skills": "代理技能筛选",
    "agents.list[].identity.avatar": "代理头像",
    "discovery.mdns.mode": "mDNS 发现模式",
    "plugins.enabled": "启用插件",
    "plugins.allow": "插件允许列表",
    "plugins.deny": "插件拒绝列表",
    "plugins.load.paths": "插件加载路径",
    "plugins.slots": "插件槽位",
    "plugins.slots.memory": "记忆插件",
    "plugins.entries": "插件条目",
    "plugins.entries.*.enabled": "插件已启用",
    "plugins.entries.*.config": "插件配置",
    "plugins.installs": "插件安装记录",
    "plugins.installs.*.source": "插件安装来源",
    "plugins.installs.*.spec": "插件安装规格",
    "plugins.installs.*.sourcePath": "插件安装源路径",
    "plugins.installs.*.installPath": "插件安装路径",
    "plugins.installs.*.version": "插件安装版本",
    "plugins.installs.*.installedAt": "插件安装时间",
  },
};

/**
 * Resolve the display label for a config field path. Uses locale-specific
 * CONFIG_FIELD_LABELS; falls back to the provided fallback (e.g. hint.label from backend).
 */
export function getConfigFieldLabel(path: Array<string | number>, fallback: string): string {
  const locale = getLocale();
  const labels = CONFIG_FIELD_LABELS[locale];
  for (const key of lookupKeys(path)) {
    const label = labels[key];
    if (label) {
      return label;
    }
  }
  return fallback;
}
